import { Button, Card } from '@citizen-walk/shared-ui';
import { useMasterHistory, type MasterHistoryEntry } from './useMasters';

/**
 * master 1 件の変更履歴 view (Phase 3.5 admin、docs/04 §3-2)。
 *
 * `/<collection>/{masterId}/history` を changedAt desc で表示 (最大 50 件、R-29 で pagination 検討)。
 * changedAt は Firestore Timestamp (server-side `serverTimestamp()` 由来)、`.toDate()` で Date 化。
 *
 * 廃止/未実装:
 *   - diff の visual highlight (現状は変更 key を一覧表示のみ)
 *   - 単一 history 詳細展開 (before/after 全文)
 *   - restore 操作 (古い version へ巻き戻し)
 */

/**
 * shared-ui tailwind-preset の利用可能 shade に合わせ、bg は薄い色 (50) or 中性 white、
 * text は強調色 (700) で badge を構成。preset に 100 shade が欠けている色は border で補強。
 */
/**
 * shared-ui tailwind-preset の利用可能 shade に合わせ、全 badge を border あり + bg は薄い
 * 色 (50/100)、text は強調色 (700/800) で構成。border 色は preset に存在する shade を
 * 選択 (preset に 100/200/700 のみ存在する色は border-700 を流用)。
 */
const TYPE_BADGE: Record<MasterHistoryEntry['changeType'], { label: string; cls: string }> = {
  create: { label: '新規', cls: 'bg-primary-100 text-primary-800 border border-primary-200' },
  update: { label: '更新', cls: 'bg-primary-50 text-primary-700 border border-primary-200' },
  archive: { label: 'アーカイブ', cls: 'bg-warning-50 text-warning-800 border border-warning-200' },
  restore: { label: '復元', cls: 'bg-notice-50 text-notice-700 border border-notice-700' },
  delete: { label: '削除', cls: 'bg-danger-50 text-danger-700 border border-danger-700' },
};

interface FirestoreTimestampLike {
  toDate?: () => Date;
  seconds?: number;
}

function formatChangedAt(value: unknown): string {
  if (!value) return '-';
  const ts = value as FirestoreTimestampLike;
  if (typeof ts.toDate === 'function') {
    return ts.toDate().toLocaleString('ja-JP');
  }
  if (typeof ts.seconds === 'number') {
    return new Date(ts.seconds * 1000).toLocaleString('ja-JP');
  }
  if (typeof value === 'string') return value;
  return '-';
}

export interface MasterHistoryViewProps {
  collectionName: string;
  masterId: string;
  masterDisplayName: string;
  /** 閉じるボタンのコールバック (parent 側で選択状態を解除)。 */
  onClose: () => void;
}

export function MasterHistoryView(props: MasterHistoryViewProps) {
  const state = useMasterHistory(props.collectionName, props.masterId);

  return (
    <Card padding="lg" className="border-slate-300 mt-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-base font-bold text-slate-900">
          履歴: {props.masterDisplayName}
          <span className="ml-2 text-xs text-slate-500 font-normal">(id: {props.masterId})</span>
        </h3>
        <Button variant="secondary" size="sm" onClick={props.onClose}>
          閉じる
        </Button>
      </div>

      {state.status === 'loading' && <p className="text-slate-500 text-sm">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600 text-sm">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && state.items.length === 0 && (
        <p className="text-slate-500 text-sm">履歴がありません。</p>
      )}
      {state.status === 'ready' && state.items.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-2">
            最新 {state.items.length} 件 (changedAt desc、最大 50)。
          </p>
          <ul className="grid grid-cols-1 gap-2">
            {state.items.map((entry) => {
              const badge = TYPE_BADGE[entry.changeType];
              const diffKeys = Object.keys(entry.diff);
              return (
                <li key={entry.id}>
                  <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className={`px-2 py-0.5 rounded font-medium ${badge.cls}`}>
                        {badge.label}
                      </span>
                      <span className="text-slate-500">v{entry.version}</span>
                      <span className="text-slate-500">{formatChangedAt(entry.changedAt)}</span>
                      <span className="text-slate-500">by {entry.changedBy}</span>
                    </div>
                    {diffKeys.length > 0 && (
                      <p className="text-xs text-slate-600 mt-2">
                        変更フィールド ({diffKeys.length}):{' '}
                        <code className="bg-white px-1 py-0.5 rounded">{diffKeys.join(', ')}</code>
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}
